// What has been spent so far: every cost entered on the Costs page that isn't void (invoiced, paid or not), before
// tax, against the budget's in-cost. In the billing currency (k 'usd', as on the Dashboard beside out-cost and margin)
// or the cost currency (k 'gbp'); the budget maths' fx[c].usd / .gbp names are historical. Shared by the Dashboard
// and the Costs page so the two always agree. Also what doesn't count towards a budget line (an amount not put on any
// line, a line that was since deleted) and the lines whose costs already pass their budget. Pure, for
// tests/unit/costs-summary.test.mjs.
import { secTotals } from './budget.js';

const r2 = n => Math.round(n * 100) / 100;

export function costsSummary(invoices, sections, fx, k = 'usd') {
  const items = (sections || []).flatMap(s => secTotals(s.items || [], fx).items);
  const lineIds = new Set(items.map(i => i.lineId).filter(Boolean));
  let net = 0, gross = 0, count = 0, paid = 0, paidCount = 0;
  const noRate = new Set(), unallocated = [], orphaned = [];
  (invoices || []).filter(i => i.status !== 'Void').forEach(inv => {
    count++;
    const allocs = inv.allocations || [];
    const onLines = allocs.reduce((s, a) => s + (Number(a.net) || 0), 0);
    // a receipt without a tax split has no net of its own: its lines' nets (worked out with each line's VAT) stand in
    const invNet = Number(inv.net) > 0 ? Number(inv.net) : onLines || Number(inv.gross) || 0;
    const rate = fx[inv.ccy]?.[k];
    if (!(rate > 0)) { noRate.add(inv.ccy || '(none)'); return; }
    net += invNet * rate; gross += (Number(inv.gross) || invNet) * rate;
    // paid: invoices marked Paid, and receipts (a receipt is for something already paid)
    if (inv.status === 'Paid' || inv.kind === 'receipt') { paid += invNet * rate; paidCount++; }
    const left = invNet - onLines;
    if (left > 0.01) unallocated.push({ id: inv.id, supplier: inv.supplier, number: inv.number, net: r2(left * rate) });
    allocs.filter(a => a.lineId && !lineIds.has(a.lineId)).forEach(a => orphaned.push({ id: inv.id, supplier: inv.supplier, number: inv.number, line: a.desc, net: r2((Number(a.net) || 0) * rate) }));
  });
  // expenses logged before Costs existed and not yet moved into it (normally none)
  items.forEach(it => { if (it.xpLine) { const f = fx[it.ccy]?.[k]; if (f > 0) { net += it.xpLine / (1 + (Number(it.vat) || 0)) * f; gross += it.xpLine * f; } } });
  const toK = it => { const f = fx[it.ccy]; return k === 'gbp' ? 1 : f && f.gbp ? f.usd / f.gbp : 0; };   // cost currency → k
  const budget = items.reduce((s, i) => s + (i.inGBP || 0) * toK(i), 0);
  const over = items.filter(i => i.varGBP != null && i.varGBP > 0.005).map(i => ({ desc: i.desc, ln: i.ln, over: r2(i.varGBP * toK(i)) }));
  return { count, net: r2(net), gross: r2(gross), paid: r2(paid), paidCount, budget: r2(budget), share: budget > 0 ? net / budget : null,
    over, overTotal: r2(over.reduce((s, o) => s + o.over, 0)), unallocated, orphaned,
    notCounted: r2([...unallocated, ...orphaned].reduce((s, x) => s + x.net, 0)), noRate: [...noRate] };
}
